import { useEffect, useReducer, useRef, useState } from "react";
import useRect from "../helpers/useRect.js";
import wrapEvent from "../helpers/wrapEvent.js";
const IDLE = 'idle';
const FOCUSED = 'focused';
const VISIBLE = 'visible';
const LEAVING_VISIBLE = 'leavingVisible';
const DISMISSED = 'dismissed';
const stateMachine = ()=>({
        initial: IDLE,
        states: {
            [IDLE]: {
                on: {
                    mouseenter: FOCUSED,
                    focus: VISIBLE
                }
            },
            [FOCUSED]: {
                on: {
                    mousemove: FOCUSED,
                    mouseleave: IDLE,
                    mousedown: DISMISSED,
                    keydown: DISMISSED,
                    blur: IDLE,
                    rest: VISIBLE
                }
            },
            [VISIBLE]: {
                on: {
                    focus: FOCUSED,
                    mouseenter: FOCUSED,
                    mouseleave: LEAVING_VISIBLE,
                    blur: LEAVING_VISIBLE,
                    mousedown: DISMISSED,
                    timecomplete: IDLE
                }
            },
            [LEAVING_VISIBLE]: {
                on: {
                    mouseenter: VISIBLE,
                    focus: VISIBLE,
                    timecomplete: IDLE
                }
            },
            [DISMISSED]: {
                on: {
                    mouseleave: IDLE,
                    blur: IDLE
                }
            }
        }
    });
const { states: states } = stateMachine();
const transition = (state, event)=>states[state]?.on[event] ?? state;
function useTooltip({ onMouseEnter, onMouseMove, onMouseLeave, onFocus, onBlur, onKeyDown, onMouseDown, ref: forwardedRef, DEBUG }) {
    const [state, dispatch] = useReducer(transition, IDLE);
    const [isVisible, setIsVisible] = useState(void 0 !== DEBUG ? DEBUG : false);
    const ownRef = useRef();
    const ref = forwardedRef || ownRef;
    const triggerRect = useRect(ref, isVisible);
    useEffect(()=>{
        if (state === FOCUSED) {
            const timer = setTimeout(()=>dispatch('rest'), 100);
            return ()=>clearTimeout(timer);
        }
        return ()=>{};
    }, [
        state
    ]);
    useEffect(()=>{
        if (state === LEAVING_VISIBLE) {
            setIsVisible(true);
            const timer = setTimeout(()=>dispatch('timecomplete'), 100);
            return ()=>clearTimeout(timer);
        }
        return ()=>{};
    }, [
        state
    ]);
    useEffect(()=>{
        if (state === VISIBLE || state === LEAVING_VISIBLE) setIsVisible(true);
        else if (!DEBUG) setIsVisible(false);
    }, [
        state,
        DEBUG
    ]);
    const handleMouseEnter = ()=>{
        switch(state){
            case IDLE:
            case VISIBLE:
            case LEAVING_VISIBLE:
                dispatch('mouseenter');
                break;
            default:
                break;
        }
    };
    const handleMouseMove = ()=>{
        switch(state){
            case FOCUSED:
                dispatch('mousemove');
                break;
            default:
                break;
        }
    };
    const handleFocus = ()=>{
        switch(state){
            case IDLE:
            case VISIBLE:
            case LEAVING_VISIBLE:
                dispatch('focus');
                break;
            default:
                break;
        }
    };
    const handleBlur = ()=>{
        switch(state){
            case FOCUSED:
            case VISIBLE:
            case DISMISSED:
                dispatch('blur');
                break;
            default:
                break;
        }
    };
    const handleMouseLeave = ()=>{
        switch(state){
            case FOCUSED:
            case VISIBLE:
            case DISMISSED:
                dispatch('mouseleave');
                break;
            default:
                break;
        }
    };
    const handleKeyDown = (event)=>{
        if ('Enter' === event.key || ' ' === event.key || 'Escape' === event.key) dispatch('keydown');
    };
    const handleMouseDown = ()=>{
        switch(state){
            case FOCUSED:
            case VISIBLE:
                dispatch('mousedown');
                break;
            default:
                break;
        }
    };
    const trigger = {
        ref,
        onMouseEnter: wrapEvent(onMouseEnter, handleMouseEnter),
        onMouseMove: wrapEvent(onMouseMove, handleMouseMove),
        onFocus: wrapEvent(onFocus, handleFocus),
        onBlur: wrapEvent(onBlur, handleBlur),
        onMouseLeave: wrapEvent(onMouseLeave, handleMouseLeave),
        onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
        onMouseDown: wrapEvent(onMouseDown, handleMouseDown)
    };
    const tooltip = {
        isVisible,
        triggerRect
    };
    return [
        trigger,
        tooltip
    ];
}
export default useTooltip;
